import React, { useState, useEffect, useRef } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { completeTraining } from './businessApi';

const BASE = process.env.REACT_APP_API_URL || '/api';

// ── Helper: extract timer duration from type string (e.g. "15 min" → 900) ──
function parseDuration(typeStr) {
  const m = typeStr.match(/(\d+)\s*min/i);
  return m ? parseInt(m[1]) * 60 : 120;
}

// ── Helper: detect practice modality from type string ──
function getPracticeMode(typeStr) {
  const t = typeStr.toLowerCase();
  if (t.includes('oefening') || t.includes('exercise')) return 'timer';
  if (t.includes('artikel') || t.includes('article')) return 'article';
  return 'steps'; // 'Training' type → guided steps
}

// ── XP by duration ──
function getXP(typeStr) {
  const mins = parseInt((typeStr.match(/(\d+)\s*min/i) || ['', '15'])[1]);
  if (mins >= 40) return 60;
  if (mins >= 25) return 45;
  if (mins >= 20) return 35;
  return 25;
}

// ── Training categories with embedded quiz data ──────────────────────────────
const CATEGORIES = [
  {
    id: 'softskills', icon: '💬', nl: 'Soft Skills', en: 'Soft Skills', color: '#5B9ED0',
    descNL: 'Communicatie, empathie en samenwerking',
    descEN: 'Communication, empathy and collaboration',
    items: [
      {
        titleNL: 'Actief Luisteren', titleEN: 'Active Listening',
        typeNL: 'Oefening · 15 min', typeEN: 'Exercise · 15 min', icon: '👂',
        bodyNL: 'Oefen volledig aanwezig zijn in gesprekken. Stel open vragen, vat samen wat de ander zegt en vermijd te onderbreken. Oefen dit vandaag in 2 gesprekken.',
        bodyEN: 'Practice being fully present in conversations. Ask open questions, summarize what the other person says, and avoid interrupting. Practice this today in 2 conversations.',
        practiceNL: 'Start een gesprek met een collega. Stel één open vraag, luister volledig en vat samen wat je gehoord hebt. Geen onderbreking, geen advies — alleen luisteren.',
        practiceEN: 'Start a conversation with a colleague. Ask one open question, listen fully and summarize what you heard. No interrupting, no advice — just listen.',
        stepsNL: ['Open houding aannemen', 'Open vraag stellen', 'Niet onderbreken', 'Samenvatten wat de ander zei', 'Doorvragen'],
        stepsEN: ['Take an open posture', 'Ask an open question', 'Do not interrupt', 'Summarize what was said', 'Ask a follow-up question'],
        quiz: [
          { qNL: 'Wat is een kernprincipe van actief luisteren?', qEN: 'What is a core principle of active listening?',
            opts: [{ nl: 'Zo snel mogelijk reageren', en: 'Respond as fast as possible' }, { nl: 'Open vragen stellen en samenvatten', en: 'Ask open questions and summarize', correct: true }, { nl: 'Altijd instemmen met de ander', en: 'Always agree with the other person' }, { nl: 'Onderbreken om begrip te tonen', en: 'Interrupt to show understanding' }] },
          { qNL: 'Hoeveel gesprekken oefener je actief luisteren per dag?', qEN: 'How many conversations do you practice active listening in per day?',
            opts: [{ nl: 'Eén vergadering per week', en: 'One meeting per week' }, { nl: '2 gesprekken per dag', en: '2 conversations per day', correct: true }, { nl: 'Alleen met je manager', en: 'Only with your manager' }, { nl: 'Eens per maand', en: 'Once per month' }] },
        ],
      },
      {
        titleNL: 'Feedback Geven', titleEN: 'Giving Feedback',
        typeNL: 'Training · 20 min', typeEN: 'Training · 20 min', icon: '🗣️',
        bodyNL: 'Gebruik de SBI-methode: Situatie → Gedrag → Impact. Beschrijf een specifieke situatie, welk gedrag je observeerde en welke impact dat had. Neutraal en feitelijk.',
        bodyEN: 'Use the SBI method: Situation → Behavior → Impact. Describe a specific situation, what behavior you observed and its impact. Neutral and factual.',
        practiceNL: 'Denk aan een recente situatie. Schrijf de SBI feedback op: Situatie (wanneer/waar), Gedrag (wat je zag), Impact (wat het veroorzaakte). Houd het neutraal.',
        practiceEN: 'Think of a recent situation. Write out the SBI feedback: Situation (when/where), Behavior (what you saw), Impact (what it caused). Keep it neutral.',
        stepsNL: ['Situatie beschrijven (wanneer, waar)', 'Gedrag beschrijven (wat je zag)', 'Impact benoemen (wat het veroorzaakte)', 'Neutraal en feitelijk houden', 'Gesprek inplannen'],
        stepsEN: ['Describe the situation (when, where)', 'Describe the behavior (what you saw)', 'Name the impact (what it caused)', 'Keep it neutral and factual', 'Schedule the conversation'],
        quiz: [
          { qNL: 'Waar staat "B" in SBI voor?', qEN: 'What does the "B" stand for in SBI?',
            opts: [{ nl: 'Beoordeling', en: 'Beoordeling' }, { nl: 'Gedrag (Behavior)', en: 'Behavior', correct: true }, { nl: 'Belang', en: 'Importance' }, { nl: 'Bron', en: 'Source' }] },
          { qNL: 'Hoe geef je feedback het beste?', qEN: 'How should you best give feedback?',
            opts: [{ nl: 'Vaag en algemeen', en: 'Vague and general' }, { nl: 'Emotioneel en persoonlijk', en: 'Emotional and personal' }, { nl: 'Specifiek, feitelijk en neutraal', en: 'Specific, factual and neutral', correct: true }, { nl: 'Alleen schriftelijk', en: 'Only in writing' }] },
        ],
      },
      {
        titleNL: 'Assertief Communiceren', titleEN: 'Assertive Communication',
        typeNL: 'Training · 25 min', typeEN: 'Training · 25 min', icon: '💪',
        bodyNL: 'Leer je behoeften en grenzen helder te communiceren. Gebruik "ik"-uitspraken: "Ik voel me... wanneer... omdat...". Assertief is noch agressief, noch passief.',
        bodyEN: 'Learn to communicate your needs and boundaries clearly. Use "I" statements: "I feel... when... because...". Assertive is neither aggressive nor passive.',
        practiceNL: 'Schrijf drie "ik"-uitspraken voor situaties waar je normaal stil zou zijn of te hard zou reageren. Oefen ze hardop uit te spreken.',
        practiceEN: 'Write three "I" statements for situations where you would normally stay silent or overreact. Practice saying them out loud.',
        stepsNL: ['Situatie identificeren', '"Ik voel me..." formuleren', '"Wanneer..." toevoegen', '"Omdat..." toevoegen', 'Hardop oefenen'],
        stepsEN: ['Identify the situation', 'Formulate "I feel..."', 'Add "when..."', 'Add "because..."', 'Practice saying it out loud'],
        quiz: [
          { qNL: 'Welke uitspraak is assertief?', qEN: 'Which statement is assertive?',
            opts: [{ nl: '"Jij maakt altijd fouten"', en: '"You always make mistakes"' }, { nl: '"Ik voel me gefrustreerd wanneer afspraken niet worden nagekomen"', en: '"I feel frustrated when agreements are not kept"', correct: true }, { nl: '"Het is prima, doe maar"', en: '"It\'s fine, go ahead"' }, { nl: '"Doe gewoon wat ik zeg"', en: '"Just do what I say"' }] },
          { qNL: 'Wat is het verschil tussen assertief en agressief?', qEN: 'What is the difference between assertive and aggressive?',
            opts: [{ nl: 'Assertief is harder spreken', en: 'Assertive means speaking louder' }, { nl: 'Er is geen verschil', en: 'There is no difference' }, { nl: 'Assertief respecteert grenzen van zichzelf én de ander', en: 'Assertive respects both your own and others\' boundaries', correct: true }, { nl: 'Agressief is effectiever', en: 'Aggressive is more effective' }] },
        ],
      },
      {
        titleNL: 'Non-verbale Communicatie', titleEN: 'Non-verbal Communication',
        typeNL: 'Artikel · 10 min', typeEN: 'Article · 10 min', icon: '🤝',
        bodyNL: '55% van communicatie is non-verbaal. Let op: oogcontact (30–60% van de tijd), open lichaamshouding, knikken en een neutrale gezichtsexpressie. Je lichaam spreekt altijd.',
        bodyEN: '55% of communication is non-verbal. Pay attention to: eye contact (30–60% of the time), open body posture, nodding and a neutral facial expression. Your body always speaks.',
        practiceNL: 'Kernpunten die je moet kennen over non-verbale communicatie:',
        practiceEN: 'Key points you need to know about non-verbal communication:',
        stepsNL: ['55% van communicatie is non-verbaal', 'Oogcontact: 30–60% van de tijd', 'Open lichaamshouding — geen gekruiste armen', 'Knikken toont begrip', 'Neutrale gezichtsexpressie bij moeilijke gesprekken'],
        stepsEN: ['55% of communication is non-verbal', 'Eye contact: 30–60% of the time', 'Open body posture — no crossed arms', 'Nodding shows understanding', 'Neutral facial expression in difficult conversations'],
        quiz: [
          { qNL: 'Welk percentage van communicatie is non-verbaal?', qEN: 'What percentage of communication is non-verbal?',
            opts: [{ nl: '7%', en: '7%' }, { nl: '38%', en: '38%' }, { nl: '55%', en: '55%', correct: true }, { nl: '90%', en: '90%' }] },
          { qNL: 'Wat is een voorbeeld van open lichaamstaal?', qEN: 'What is an example of open body language?',
            opts: [{ nl: 'Armen gekruist', en: 'Arms crossed' }, { nl: 'Oogcontact vermijden', en: 'Avoiding eye contact' }, { nl: 'Open houding en knikken', en: 'Open posture and nodding', correct: true }, { nl: 'Op de telefoon kijken', en: 'Looking at your phone' }] },
        ],
      },
    ],
  },
  {
    id: 'hardskills', icon: '🎯', nl: 'Hard Skills', en: 'Hard Skills', color: '#C8A84B',
    descNL: 'Professionele vaardigheden en kennisverdieping',
    descEN: 'Professional skills and knowledge development',
    items: [
      {
        titleNL: 'Time Management', titleEN: 'Time Management',
        typeNL: 'Training · 30 min', typeEN: 'Training · 30 min', icon: '⏰',
        bodyNL: 'Gebruik de Eisenhower-matrix: Urgent + Belangrijk → direct doen. Belangrijk + Niet urgent → inplannen. Gebruik tijdblokken van 90 minuten voor diepwerk.',
        bodyEN: 'Use the Eisenhower matrix: Urgent + Important → do now. Important + Not urgent → schedule. Use 90-minute time blocks for deep work.',
        practiceNL: 'Sorteer je taken van vandaag in de Eisenhower-matrix. Identificeer één "Belangrijk maar niet urgent" taak en plan een 90-minuten blok in je agenda.',
        practiceEN: 'Sort today\'s tasks in the Eisenhower matrix. Identify one "Important but not urgent" task and schedule a 90-minute block in your calendar.',
        stepsNL: ['Lijst alle taken van vandaag', 'Eisenhower-matrix toepassen', '"Urgent + Belangrijk" direct aanpakken', '"Belangrijk + Niet urgent" inplannen', '90-minuten diepwerkblok plannen'],
        stepsEN: ['List all tasks for today', 'Apply the Eisenhower matrix', 'Address "Urgent + Important" first', 'Schedule "Important + Not urgent"', 'Plan a 90-minute deep work block'],
        quiz: [
          { qNL: 'Welke matrix helpt bij het prioriteren van taken?', qEN: 'Which matrix helps prioritize tasks?',
            opts: [{ nl: 'BCG-matrix', en: 'BCG matrix' }, { nl: 'Eisenhower-matrix', en: 'Eisenhower matrix', correct: true }, { nl: 'SWOT-analyse', en: 'SWOT analysis' }, { nl: 'Ansoff-matrix', en: 'Ansoff matrix' }] },
          { qNL: 'Hoe lang zijn ideale diepwerkblokken?', qEN: 'How long are ideal deep work blocks?',
            opts: [{ nl: '30 minuten', en: '30 minutes' }, { nl: '45 minuten', en: '45 minutes' }, { nl: '90 minuten', en: '90 minutes', correct: true }, { nl: '3 uur', en: '3 hours' }] },
        ],
      },
      {
        titleNL: 'Data-analyse Basis', titleEN: 'Data Analysis Basics',
        typeNL: 'Oefening · 45 min', typeEN: 'Exercise · 45 min', icon: '📊',
        bodyNL: 'Leer hoe je data gebruikt voor beslissingen. Start met het begrijpen van gemiddelden, trends en uitschieters. Stel jezelf altijd de vraag: Wat vertelt deze data mij?',
        bodyEN: 'Learn how to use data for decisions. Start by understanding averages, trends and outliers. Always ask yourself: What is this data telling me?',
        practiceNL: 'Kijk naar een dataset die je momenteel hebt (verkoopcijfers, scores, enquêtes). Identificeer: het gemiddelde, een trend over tijd en één uitschieter.',
        practiceEN: 'Look at a dataset you currently have (sales figures, scores, surveys). Identify: the average, a trend over time and one outlier.',
        practiceMode: 'timer',
        stepsNL: [], stepsEN: [],
        quiz: [
          { qNL: 'Wat is de eerste vraag bij data-analyse?', qEN: 'What is the first question in data analysis?',
            opts: [{ nl: 'Hoe maak ik een mooie grafiek?', en: 'How do I make a nice chart?' }, { nl: 'Welk programma gebruik ik?', en: 'Which software should I use?' }, { nl: 'Wat vertelt deze data mij?', en: 'What is this data telling me?', correct: true }, { nl: 'Klopt de kleur van de grafiek?', en: 'Is the chart color correct?' }] },
          { qNL: 'Wat zijn de drie basiselementen van data-analyse?', qEN: 'What are the three basic elements of data analysis?',
            opts: [{ nl: 'Letters, cijfers, symbolen', en: 'Letters, numbers, symbols' }, { nl: 'Gemiddelden, trends en uitschieters', en: 'Averages, trends and outliers', correct: true }, { nl: 'Input, proces, output', en: 'Input, process, output' }, { nl: 'Hoog, midden, laag', en: 'High, medium, low' }] },
        ],
      },
      {
        titleNL: 'Projectmanagement', titleEN: 'Project Management',
        typeNL: 'Training · 40 min', typeEN: 'Training · 40 min', icon: '📋',
        bodyNL: 'Gebruik een eenvoudige Kanban-bord methodiek: To Do → In Progress → Done. Definieer heldere mijlpalen en houd wekelijkse reviews om voortgang te beoordelen.',
        bodyEN: 'Use a simple Kanban board methodology: To Do → In Progress → Done. Define clear milestones and hold weekly reviews to assess progress.',
        practiceNL: 'Maak een Kanban-bord voor je huidige project. Definieer 3 mijlpalen en plan een wekelijkse 15-minuten review in je agenda.',
        practiceEN: 'Create a Kanban board for your current project. Define 3 milestones and schedule a weekly 15-minute review in your calendar.',
        stepsNL: ['Huidig project identificeren', 'Kanban-bord aanmaken (To Do / In Progress / Done)', '3 mijlpalen definiëren', 'Taken verdelen over de kolommen', 'Wekelijkse review inplannen'],
        stepsEN: ['Identify your current project', 'Create Kanban board (To Do / In Progress / Done)', 'Define 3 milestones', 'Distribute tasks across columns', 'Schedule a weekly review'],
        quiz: [
          { qNL: 'Welke methodiek gebruikt een To Do → In Progress → Done bord?', qEN: 'Which methodology uses a To Do → In Progress → Done board?',
            opts: [{ nl: 'Waterfall', en: 'Waterfall' }, { nl: 'PRINCE2', en: 'PRINCE2' }, { nl: 'Kanban', en: 'Kanban', correct: true }, { nl: 'Six Sigma', en: 'Six Sigma' }] },
          { qNL: 'Hoe vaak voer je een projectreview uit?', qEN: 'How often should you conduct a project review?',
            opts: [{ nl: 'Maandelijks', en: 'Monthly' }, { nl: 'Dagelijks', en: 'Daily' }, { nl: 'Wekelijks', en: 'Weekly', correct: true }, { nl: 'Eén keer aan het einde', en: 'Once at the end' }] },
        ],
      },
      {
        titleNL: 'Presentatievaardigheden', titleEN: 'Presentation Skills',
        typeNL: 'Oefening · 35 min', typeEN: 'Exercise · 35 min', icon: '🎤',
        bodyNL: 'Gebruik de 10-20-30 regel van Guy Kawasaki: maximaal 10 slides, 20 minuten, minimale lettergrootte 30pt. Begin altijd met het "waarom" voor je publiek.',
        bodyEN: 'Use Guy Kawasaki\'s 10-20-30 rule: maximum 10 slides, 20 minutes, minimum 30pt font size. Always start with the "why" for your audience.',
        practiceNL: 'Bereid een 2-minuten elevator pitch voor over jouw afdeling of project. Volg de structuur: Waarom → Wat → Hoe. Oefen het hardop 3 keer.',
        practiceEN: 'Prepare a 2-minute elevator pitch about your department or project. Follow the structure: Why → What → How. Practice saying it out loud 3 times.',
        stepsNL: ['Bepaal je "waarom" (doel voor het publiek)', 'Structureer: Waarom → Wat → Hoe', 'Maximaal 10 slides', '30pt lettergrootte minimum', '3× hardop oefenen'],
        stepsEN: ['Define your "why" (goal for the audience)', 'Structure: Why → What → How', 'Maximum 10 slides', '30pt font size minimum', 'Practice out loud 3 times'],
        quiz: [
          { qNL: 'Wat is de 10-20-30 regel?', qEN: 'What is the 10-20-30 rule?',
            opts: [{ nl: '10 minuten, 20 slides, 30 woorden per slide', en: '10 minutes, 20 slides, 30 words per slide' }, { nl: '10 slides, 20 minuten, lettergrootte 30pt', en: '10 slides, 20 minutes, 30pt font size', correct: true }, { nl: '10 vragen, 20 antwoorden, 30 seconden per slide', en: '10 questions, 20 answers, 30 seconds per slide' }, { nl: '10 punten, 20 voorbeelden, 30 minuten', en: '10 points, 20 examples, 30 minutes' }] },
          { qNL: 'Waarmee begin je altijd een goede presentatie?', qEN: 'With what do you always start a good presentation?',
            opts: [{ nl: 'Met een moeilijke vraag', en: 'With a difficult question' }, { nl: 'Met je naam en functie', en: 'With your name and title' }, { nl: 'Met het "waarom" voor je publiek', en: 'With the "why" for your audience', correct: true }, { nl: 'Met een grap', en: 'With a joke' }] },
        ],
      },
    ],
  },
  {
    id: 'social', icon: '🤝', nl: 'Social Skills', en: 'Social Skills', color: '#2A9E82',
    descNL: 'Relaties bouwen en teamdynamiek versterken',
    descEN: 'Building relationships and strengthening team dynamics',
    items: [
      {
        titleNL: 'Conflicthantering', titleEN: 'Conflict Resolution',
        typeNL: 'Training · 30 min', typeEN: 'Training · 30 min', icon: '⚖️',
        bodyNL: 'Gebruik de DEAR-methode: Describe de situatie feitelijk, Express je gevoel, Assert wat je wilt, Reinforce met positieve gevolgen. Focus op belangen, niet posities.',
        bodyEN: 'Use the DEAR method: Describe the situation factually, Express your feeling, Assert what you want, Reinforce with positive consequences. Focus on interests, not positions.',
        practiceNL: 'Denk aan een lopend conflict of meningsverschil. Schrijf de DEAR-stappen uit voor die situatie.',
        practiceEN: 'Think of an ongoing conflict or disagreement. Write out the DEAR steps for that situation.',
        stepsNL: ['D – Situatie feitelijk beschrijven', 'E – Je gevoel uitdrukken', 'A – Assert wat je wilt', 'R – Reinforce met positieve gevolgen', 'Focus op belangen, niet posities'],
        stepsEN: ['D – Describe the situation factually', 'E – Express your feeling', 'A – Assert what you want', 'R – Reinforce with positive consequences', 'Focus on interests, not positions'],
        quiz: [
          { qNL: 'Waar staat DEAR voor?', qEN: 'What does DEAR stand for?',
            opts: [{ nl: 'Do, Expect, Act, React', en: 'Do, Expect, Act, React' }, { nl: 'Describe, Express, Assert, Reinforce', en: 'Describe, Express, Assert, Reinforce', correct: true }, { nl: 'Define, Evaluate, Assess, Report', en: 'Define, Evaluate, Assess, Report' }, { nl: 'Direct, Empathize, Agree, Resolve', en: 'Direct, Empathize, Agree, Resolve' }] },
          { qNL: 'Waar focus je op bij conflictoplossing?', qEN: 'What do you focus on in conflict resolution?',
            opts: [{ nl: 'Posities', en: 'Positions' }, { nl: 'Schuld aanwijzen', en: 'Assigning blame' }, { nl: 'Belangen van beide partijen', en: 'Interests of both parties', correct: true }, { nl: 'Wie het hardst schreeuwt', en: 'Who shouts loudest' }] },
        ],
      },
      {
        titleNL: 'Teamvertrouwen Opbouwen', titleEN: 'Building Team Trust',
        typeNL: 'Oefening · 20 min', typeEN: 'Exercise · 20 min', icon: '🌱',
        bodyNL: 'Vertrouwen bouw je door: consistentie (doe wat je zegt), transparantie (deel informatie proactief) en kwetsbaarheid tonen (durf fouten te erkennen).',
        bodyEN: 'You build trust through: consistency (do what you say), transparency (share information proactively) and showing vulnerability (dare to acknowledge mistakes).',
        practiceNL: 'Neem 20 minuten om vertrouwen actief op te bouwen in je team. Kies één actie uit elke pijler.',
        practiceEN: 'Take 20 minutes to actively build trust in your team. Choose one action from each pillar.',
        stepsNL: ['Consistentie: nakomen van één belofte van vandaag', 'Transparantie: proactief informatie delen', 'Kwetsbaarheid: een fout of twijfel erkennen', 'Collega oprecht complimenteren', 'Check-in plannen met een teamlid'],
        stepsEN: ['Consistency: follow through on one promise today', 'Transparency: proactively share information', 'Vulnerability: acknowledge a mistake or doubt', 'Genuinely compliment a colleague', 'Plan a check-in with a team member'],
        quiz: [
          { qNL: 'Welke drie pijlers bouwen vertrouwen op?', qEN: 'Which three pillars build trust?',
            opts: [{ nl: 'Snelheid, kwaliteit, prijs', en: 'Speed, quality, price' }, { nl: 'Consistentie, transparantie, kwetsbaarheid', en: 'Consistency, transparency, vulnerability', correct: true }, { nl: 'Controle, regels, structuur', en: 'Control, rules, structure' }, { nl: 'Charisma, intelligentie, ervaring', en: 'Charisma, intelligence, experience' }] },
          { qNL: 'Wat betekent kwetsbaarheid in een team?', qEN: 'What does vulnerability mean in a team?',
            opts: [{ nl: 'Zwakte tonen', en: 'Showing weakness' }, { nl: 'Fouten durven erkennen', en: 'Daring to acknowledge mistakes', correct: true }, { nl: 'Altijd hulp vragen', en: 'Always asking for help' }, { nl: 'Nooit een mening hebben', en: 'Never having an opinion' }] },
        ],
      },
      {
        titleNL: 'Empathie Ontwikkelen', titleEN: 'Developing Empathy',
        typeNL: 'Artikel · 15 min', typeEN: 'Article · 15 min', icon: '❤️',
        bodyNL: 'Cognitieve empathie: verplaats je in de situatie van de ander. Stel jezelf voor: Wat ervaart deze persoon? Wat zijn hun zorgen? Reageer vanuit begrip, niet vanuit oordeel.',
        bodyEN: 'Cognitive empathy: put yourself in the other person\'s situation. Ask yourself: What is this person experiencing? What are their concerns? Respond from understanding, not judgment.',
        practiceNL: 'Kernpunten over het ontwikkelen van empathie:',
        practiceEN: 'Key points about developing empathy:',
        stepsNL: ['Cognitieve empathie = je verplaatsen in de ander', 'Stel jezelf: "Wat ervaart deze persoon?"', 'Vraag naar zorgen — niet naar oplossingen', 'Reageer vanuit begrip, niet oordeel', 'Vermijd "dat had ik ook" — luister eerst'],
        stepsEN: ['Cognitive empathy = putting yourself in another\'s position', 'Ask yourself: "What is this person experiencing?"', 'Ask about concerns — not solutions', 'Respond from understanding, not judgment', 'Avoid "same happened to me" — listen first'],
        quiz: [
          { qNL: 'Wat is cognitieve empathie?', qEN: 'What is cognitive empathy?',
            opts: [{ nl: 'Mee huilen met de ander', en: 'Crying along with the other person' }, { nl: 'Je verplaatsen in de situatie van de ander', en: 'Putting yourself in the other person\'s situation', correct: true }, { nl: 'Advies geven vanuit eigen ervaring', en: 'Giving advice from personal experience' }, { nl: 'Oplossingen aandragen', en: 'Offering solutions' }] },
          { qNL: 'Hoe reageer je empatisch?', qEN: 'How do you respond empathetically?',
            opts: [{ nl: 'Vanuit oordeel', en: 'From judgment' }, { nl: 'Met directe oplossingen', en: 'With direct solutions' }, { nl: 'Vanuit begrip voor de ander', en: 'From understanding of the other person', correct: true }, { nl: 'Door het probleem te minimaliseren', en: 'By minimizing the problem' }] },
        ],
      },
      {
        titleNL: 'Netwerken & Samenwerken', titleEN: 'Networking & Collaboration',
        typeNL: 'Training · 25 min', typeEN: 'Training · 25 min', icon: '🔗',
        bodyNL: 'Effectief netwerken: focus op waarde geven voor ontvangen. Stel vragen, luister, onthoud namen en follow-up binnen 24 uur. Interne samenwerking: clarificeer rollen en verwachtingen vooraf.',
        bodyEN: 'Effective networking: focus on giving value before receiving. Ask questions, listen, remember names and follow-up within 24 hours. Internal collaboration: clarify roles and expectations upfront.',
        practiceNL: 'Identificeer één persoon in je netwerk die je deze week kunt helpen. Stuur een bericht of plan een kort gesprek in.',
        practiceEN: 'Identify one person in your network you can help this week. Send a message or schedule a short conversation.',
        stepsNL: ['Één persoon identificeren die je kunt helpen', 'Waarde aanbieden (info, introductie, hulp)', 'Naam en functie onthouden', 'Follow-up bericht sturen binnen 24 uur', 'Interne samenwerking: rollen clarificeren'],
        stepsEN: ['Identify one person you can help', 'Offer value (info, introduction, help)', 'Remember name and role', 'Send a follow-up message within 24 hours', 'Internal collaboration: clarify roles'],
        quiz: [
          { qNL: 'Wat is de sleutel tot effectief netwerken?', qEN: 'What is the key to effective networking?',
            opts: [{ nl: 'Zoveel mogelijk visitekaartjes verzamelen', en: 'Collecting as many business cards as possible' }, { nl: 'Eerst waarde geven voor je ontvangt', en: 'Give value before you receive', correct: true }, { nl: 'Altijd over jezelf praten', en: 'Always talk about yourself' }, { nl: 'Alleen contact zoeken als je iets nodig hebt', en: 'Only reach out when you need something' }] },
          { qNL: 'Wanneer doe je follow-up na netwerken?', qEN: 'When do you follow up after networking?',
            opts: [{ nl: 'Na een week', en: 'After a week' }, { nl: 'Na een maand', en: 'After a month' }, { nl: 'Binnen 24 uur', en: 'Within 24 hours', correct: true }, { nl: 'Ze nemen contact op', en: 'They will reach out' }] },
        ],
      },
    ],
  },
  {
    id: 'focus', icon: '🧠', nl: 'Focus', en: 'Focus', color: '#9B59B6',
    descNL: 'Concentratie, mindset en mentale kracht',
    descEN: 'Concentration, mindset and mental strength',
    items: [
      {
        titleNL: 'Deep Work Techniek', titleEN: 'Deep Work Technique',
        typeNL: 'Training · 25 min', typeEN: 'Training · 25 min', icon: '🔬',
        bodyNL: 'Plan dagelijks 2–4 uur voor deep work: geen meldingen, één taak, volledige concentratie. Gebruik de Pomodoro-techniek: 25 min werk, 5 min pauze, na 4 rondes 20 min pauze.',
        bodyEN: 'Plan 2–4 hours daily for deep work: no notifications, one task, full concentration. Use the Pomodoro technique: 25 min work, 5 min break, after 4 rounds a 20 min break.',
        practiceNL: 'Start nu een Pomodoro-sessie van 25 minuten. Zet alle meldingen uit, kies één taak en focus volledig.',
        practiceEN: 'Start a 25-minute Pomodoro session now. Turn off all notifications, choose one task and focus completely.',
        stepsNL: ['Alle meldingen uitschakelen', 'Één specifieke taak kiezen', '25 minuten timer starten', 'Geen onderbrekingen', '5 minuten pauze na de sessie'],
        stepsEN: ['Turn off all notifications', 'Choose one specific task', 'Start a 25-minute timer', 'No interruptions', '5-minute break after the session'],
        quiz: [
          { qNL: 'Hoe lang is een Pomodoro-sessie?', qEN: 'How long is a Pomodoro session?',
            opts: [{ nl: '15 min werk, 5 min pauze', en: '15 min work, 5 min break' }, { nl: '25 min werk, 5 min pauze', en: '25 min work, 5 min break', correct: true }, { nl: '45 min werk, 15 min pauze', en: '45 min work, 15 min break' }, { nl: '60 min werk, 10 min pauze', en: '60 min work, 10 min break' }] },
          { qNL: 'Hoeveel uur deep work per dag is aanbevolen?', qEN: 'How many hours of deep work per day is recommended?',
            opts: [{ nl: '30 minuten', en: '30 minutes' }, { nl: '1 uur', en: '1 hour' }, { nl: '2–4 uur', en: '2–4 hours', correct: true }, { nl: '8 uur', en: '8 hours' }] },
        ],
      },
      {
        titleNL: 'Mindfulness op het Werk', titleEN: 'Mindfulness at Work',
        typeNL: 'Oefening · 10 min', typeEN: 'Exercise · 10 min', icon: '🧘',
        bodyNL: 'Doe 2 keer per dag een 5-minuten ademhalingsoefening: adem 4 seconden in, houd 4 seconden vast, adem 6 seconden uit. Dit activeert het parasympathische zenuwstelsel.',
        bodyEN: 'Do a 5-minute breathing exercise twice a day: breathe in for 4 seconds, hold for 4 seconds, breathe out for 6 seconds. This activates the parasympathetic nervous system.',
        practiceNL: 'Doe nu een geleide ademhalingsoefening. Volg het ritme op het scherm.',
        practiceEN: 'Do a guided breathing exercise now. Follow the rhythm on screen.',
        stepsNL: [], stepsEN: [],
        quiz: [
          { qNL: 'Wat is het ritme van de aanbevolen ademhalingsoefening?', qEN: 'What is the rhythm of the recommended breathing exercise?',
            opts: [{ nl: '4 in, 4 vast, 4 uit', en: '4 in, 4 hold, 4 out' }, { nl: '6 in, 2 vast, 6 uit', en: '6 in, 2 hold, 6 out' }, { nl: '4 in, 4 vast, 6 uit', en: '4 in, 4 hold, 6 out', correct: true }, { nl: '8 in, 8 vast, 8 uit', en: '8 in, 8 hold, 8 out' }] },
          { qNL: 'Hoe vaak per dag doe je de ademhalingsoefening?', qEN: 'How many times per day do you do the breathing exercise?',
            opts: [{ nl: 'Eén keer', en: 'Once' }, { nl: 'Twee keer', en: 'Twice', correct: true }, { nl: 'Vijf keer', en: 'Five times' }, { nl: 'Alleen bij stress', en: 'Only when stressed' }] },
        ],
      },
      {
        titleNL: 'Groeimindset', titleEN: 'Growth Mindset',
        typeNL: 'Artikel · 20 min', typeEN: 'Article · 20 min', icon: '🌱',
        bodyNL: 'Dweck\'s groeimindset: intelligentie en vaardigheden zijn te ontwikkelen. Vervang "Ik kan dit niet" door "Ik kan dit nog niet." Zie uitdagingen als leerkansen, fouten als feedback.',
        bodyEN: 'Dweck\'s growth mindset: intelligence and skills can be developed. Replace "I can\'t do this" with "I can\'t do this yet." See challenges as learning opportunities, mistakes as feedback.',
        practiceNL: 'Kernpunten van de groeimindset die je moet kennen:',
        practiceEN: 'Core principles of the growth mindset you need to know:',
        stepsNL: ['Intelligentie is ontwikkelbaar (niet vaststaand)', '"Ik kan dit niet" → "Ik kan dit nog niet"', 'Uitdagingen zijn leerkansen', 'Fouten zijn feedback, geen falen', 'Inspanning en strategie bepalen succes'],
        stepsEN: ['Intelligence is developable (not fixed)', '"I can\'t do this" → "I can\'t do this yet"', 'Challenges are learning opportunities', 'Mistakes are feedback, not failure', 'Effort and strategy determine success'],
        quiz: [
          { qNL: 'Wie introduceerde het concept van de groeimindset?', qEN: 'Who introduced the concept of the growth mindset?',
            opts: [{ nl: 'Sigmund Freud', en: 'Sigmund Freud' }, { nl: 'Carol Dweck', en: 'Carol Dweck', correct: true }, { nl: 'Albert Einstein', en: 'Albert Einstein' }, { nl: 'Malcolm Gladwell', en: 'Malcolm Gladwell' }] },
          { qNL: 'Hoe vervang je een vaste mindset gedachte?', qEN: 'How do you replace a fixed mindset thought?',
            opts: [{ nl: '"Ik kan dit niet" → "Dit is te moeilijk"', en: '"I can\'t" → "This is too hard"' }, { nl: '"Ik kan dit niet" → "Anderen kunnen het ook niet"', en: '"I can\'t" → "Others can\'t either"' }, { nl: '"Ik kan dit niet" → "Ik kan dit nog niet"', en: '"I can\'t" → "I can\'t yet"', correct: true }, { nl: '"Ik kan dit niet" → "Ik ga het niet proberen"', en: '"I can\'t" → "I won\'t try"' }] },
        ],
      },
      {
        titleNL: 'Energie Management', titleEN: 'Energy Management',
        typeNL: 'Training · 30 min', typeEN: 'Training · 30 min', icon: '⚡',
        bodyNL: 'Beheer je vier energiebronnen: fysiek (slaap, beweging, voeding), emotioneel (positieve relaties), mentaal (focus, pauzes), spiritueel (zingeving, waarden). Plan herstel net zo bewust als werk.',
        bodyEN: 'Manage your four energy sources: physical (sleep, movement, nutrition), emotional (positive relationships), mental (focus, breaks), spiritual (purpose, values). Plan recovery as intentionally as work.',
        practiceNL: 'Doe een energie-audit: beoordeel elk van de 4 bronnen van 1-10 en plan één herstelactie per bron voor deze week.',
        practiceEN: 'Do an energy audit: rate each of the 4 sources 1-10 and plan one recovery action per source for this week.',
        stepsNL: ['Fysiek: beoordeel slaap/beweging/voeding (1-10)', 'Emotioneel: beoordeel relaties/steun (1-10)', 'Mentaal: beoordeel focus/pauzes (1-10)', 'Spiritueel: beoordeel zingeving/waarden (1-10)', 'Per bron: één herstelactie inplannen'],
        stepsEN: ['Physical: rate sleep/movement/nutrition (1-10)', 'Emotional: rate relationships/support (1-10)', 'Mental: rate focus/breaks (1-10)', 'Spiritual: rate purpose/values (1-10)', 'Per source: plan one recovery action'],
        quiz: [
          { qNL: 'Hoeveel energiebronnen beheer je?', qEN: 'How many energy sources do you manage?',
            opts: [{ nl: '2 (fysiek en mentaal)', en: '2 (physical and mental)' }, { nl: '3 (fysiek, emotioneel, mentaal)', en: '3 (physical, emotional, mental)' }, { nl: '4 (fysiek, emotioneel, mentaal, spiritueel)', en: '4 (physical, emotional, mental, spiritual)', correct: true }, { nl: '5 (inclusief financieel)', en: '5 (including financial)' }] },
          { qNL: 'Wat betekent "spirituele" energie in dit model?', qEN: 'What does "spiritual" energy mean in this model?',
            opts: [{ nl: 'Religieuze activiteiten', en: 'Religious activities' }, { nl: 'Yoga en meditatie', en: 'Yoga and meditation' }, { nl: 'Zingeving en waarden', en: 'Purpose and values', correct: true }, { nl: 'Slaap en rust', en: 'Sleep and rest' }] },
        ],
      },
    ],
  },
];

// ── Phase bar ────────────────────────────────────────────────────────────────
function PhaseBar({ phase, color, nl }) {
  const phases = ['read', 'practice', 'quiz', 'done'];
  const labels = nl ? ['Lezen', 'Oefenen', 'Quiz', 'Klaar'] : ['Read', 'Practice', 'Quiz', 'Done'];
  const current = phases.indexOf(phase);
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
      {phases.map((p, i) => (
        <div key={p} style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ height: 4, borderRadius: 2, background: current >= i ? color : `${C.silver}20`, marginBottom: 4, transition: 'background 0.3s' }} />
          <div style={{ fontSize: 9, fontWeight: current >= i ? 700 : 400, color: current >= i ? color : C.silver2 }}>{labels[i]}</div>
        </div>
      ))}
    </div>
  );
}

// ── Breathing exercise (mindfulness item) ────────────────────────────────────
function BreathingExercise({ nl, onDone }) {
  const CYCLE = [
    { label: nl ? 'Inademen' : 'Breathe in', secs: 4, scale: 1.35 },
    { label: nl ? 'Vasthouden' : 'Hold', secs: 4, scale: 1.35 },
    { label: nl ? 'Uitademen' : 'Breathe out', secs: 6, scale: 1.0 },
  ];
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [countdown, setCountdown] = useState(CYCLE[0].secs);
  const [cycles, setCycles] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          setPhaseIdx(p => {
            const next = (p + 1) % CYCLE.length;
            if (next === 0) setCycles(cy => cy + 1);
            setCountdown(CYCLE[next].secs);
            return next;
          });
          return CYCLE[0].secs;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const phase = CYCLE[phaseIdx];
  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ position: 'relative', width: 160, height: 160, margin: '0 auto 24px' }}>
        <div style={{
          width: 160, height: 160, borderRadius: '50%',
          background: `radial-gradient(circle, #9B59B644, #9B59B611)`,
          border: '2px solid #9B59B666',
          transform: `scale(${phase.scale})`,
          transition: `transform ${phase.secs * 0.9}s ease-in-out`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div>
            <div style={{ fontSize: 32 }}>🧘</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#9B59B6', marginTop: 4 }}>{countdown}</div>
          </div>
        </div>
      </div>
      <div style={{ color: '#9B59B6', fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{phase.label}</div>
      <div style={{ color: C.silver, fontSize: 12, marginBottom: 24 }}>
        {nl ? `Cyclus ${cycles + 1} van 3` : `Cycle ${cycles + 1} of 3`}
      </div>
      {cycles >= 3 && (
        <button onClick={onDone}
          style={{ background: 'linear-gradient(135deg,#9B59B6,#7d45a1)', border: 'none', borderRadius: 12, color: '#fff', fontSize: 14, fontWeight: 700, padding: '12px 28px', cursor: 'pointer' }}>
          ✓ {nl ? 'Oefening voltooid' : 'Exercise complete'}
        </button>
      )}
      {cycles < 3 && (
        <button onClick={onDone}
          style={{ background: 'none', border: `1px solid ${C.silver}33`, borderRadius: 10, color: C.silver, fontSize: 12, padding: '8px 18px', cursor: 'pointer' }}>
          {nl ? 'Overslaan' : 'Skip'}
        </button>
      )}
    </div>
  );
}

// ── Countdown timer practice ──────────────────────────────────────────────────
function TimerPractice({ item, nl, color, onDone }) {
  const totalSecs = parseDuration(nl ? item.typeNL : item.typeEN);
  const [secs, setSecs] = useState(totalSecs);
  const [running, setRunning] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (running) {
      ref.current = setInterval(() => setSecs(s => { if (s <= 1) { clearInterval(ref.current); setRunning(false); return 0; } return s - 1; }), 1000);
    } else {
      clearInterval(ref.current);
    }
    return () => clearInterval(ref.current);
  }, [running]);

  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');
  const pct = 1 - secs / totalSecs;

  return (
    <div style={{ textAlign: 'center', padding: '10px 0' }}>
      <div style={{ color: C.silver2, fontSize: 13, lineHeight: 1.7, marginBottom: 20, textAlign: 'left', background: `${_T.bg3}`, borderRadius: 12, padding: 14, border: `1px solid ${_T.cardBorderBot}` }}>
        {nl ? item.practiceNL : item.practiceEN}
      </div>
      <div style={{ position: 'relative', width: 130, height: 130, margin: '0 auto 20px' }}>
        <svg width="130" height="130" style={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }}>
          <circle cx="65" cy="65" r="56" fill="none" stroke={`${color}20`} strokeWidth="8"/>
          <circle cx="65" cy="65" r="56" fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={`${2 * Math.PI * 56}`} strokeDashoffset={`${2 * Math.PI * 56 * (1 - pct)}`}
            strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s linear' }}/>
        </svg>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{mm}:{ss}</div>
          <div style={{ fontSize: 10, color: C.silver }}>{nl ? 'resterend' : 'remaining'}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
        <button onClick={() => setRunning(r => !r)}
          style={{ background: running ? `${color}20` : color, border: `1px solid ${color}`, borderRadius: 10, color: running ? color : '#0a1220', fontSize: 13, fontWeight: 700, padding: '10px 22px', cursor: 'pointer' }}>
          {running ? (nl ? '⏸ Pauze' : '⏸ Pause') : (nl ? '▶ Start' : '▶ Start')}
        </button>
      </div>
      <button onClick={onDone}
        style={{ background: secs === 0 ? `linear-gradient(135deg,${color},${color}cc)` : 'none', border: secs === 0 ? 'none' : `1px solid ${C.silver}33`, borderRadius: 10, color: secs === 0 ? '#0a1220' : C.silver, fontSize: 13, fontWeight: secs === 0 ? 700 : 400, padding: '10px 22px', cursor: 'pointer' }}>
        {secs === 0 ? (nl ? '✓ Klaar!' : '✓ Done!') : (nl ? 'Overslaan' : 'Skip')}
      </button>
    </div>
  );
}

// ── Steps practice (Training type) ───────────────────────────────────────────
function StepsPractice({ item, nl, color, onDone }) {
  const steps = nl ? item.stepsNL : item.stepsEN;
  const [checked, setChecked] = useState([]);
  const toggle = (i) => setChecked(c => c.includes(i) ? c.filter(x => x !== i) : [...c, i]);
  const allDone = checked.length === steps.length;

  return (
    <div>
      <div style={{ color: C.silver2, fontSize: 13, lineHeight: 1.7, marginBottom: 16, background: _T.bg3, borderRadius: 12, padding: 14, border: `1px solid ${_T.cardBorderBot}` }}>
        {nl ? item.practiceNL : item.practiceEN}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {steps.map((step, i) => {
          const done = checked.includes(i);
          return (
            <button key={i} onClick={() => toggle(i)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, background: done ? `${color}12` : _T.bg3, border: `1.5px solid ${done ? color : _T.cardBorderBot}`, borderRadius: 12, padding: '12px 14px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
              <div style={{ width: 24, height: 24, borderRadius: 7, border: `2px solid ${done ? color : _T.cardBorderBot}`, background: done ? color : _T.bg4, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}>
                {done && <span style={{ color: '#0a1220', fontSize: 13, fontWeight: 700 }}>✓</span>}
              </div>
              <span style={{ fontSize: 13, color: done ? color : C.white, fontWeight: done ? 600 : 400, textDecoration: done ? 'line-through' : 'none' }}>{step}</span>
            </button>
          );
        })}
      </div>
      <button onClick={onDone} disabled={!allDone}
        style={{ width: '100%', padding: 13, background: allDone ? `linear-gradient(135deg,${color},${color}cc)` : `${C.silver}15`, border: allDone ? 'none' : `1px solid ${C.silver}22`, borderRadius: 12, color: allDone ? '#0a1220' : C.silver2, fontSize: 14, fontWeight: 700, cursor: allDone ? 'pointer' : 'default', transition: 'all 0.2s' }}>
        {allDone ? (nl ? '✓ Alle stappen voltooid!' : '✓ All steps complete!') : `${checked.length}/${steps.length} ${nl ? 'stappen' : 'steps'}`}
      </button>
    </div>
  );
}

// ── Article key-points practice ───────────────────────────────────────────────
function ArticlePractice({ item, nl, color, onDone }) {
  const steps = nl ? item.stepsNL : item.stepsEN;
  const [understood, setUnderstood] = useState([]);
  const toggle = (i) => setUnderstood(u => u.includes(i) ? u.filter(x => x !== i) : [...u, i]);
  const allDone = understood.length === steps.length;

  return (
    <div>
      <div style={{ color: C.silver, fontSize: 12, marginBottom: 12, fontWeight: 600 }}>
        {nl ? 'Markeer elk punt als begrepen:' : 'Mark each point as understood:'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {steps.map((point, i) => {
          const done = understood.includes(i);
          return (
            <button key={i} onClick={() => toggle(i)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, background: done ? `${color}10` : _T.bg3, border: `1.5px solid ${done ? color : _T.cardBorderBot}`, borderRadius: 10, padding: '11px 14px', cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ fontSize: 16, flexShrink: 0 }}>{done ? '✅' : '○'}</div>
              <span style={{ fontSize: 13, color: done ? color : C.silver2 }}>{point}</span>
            </button>
          );
        })}
      </div>
      <button onClick={onDone} disabled={!allDone}
        style={{ width: '100%', padding: 13, background: allDone ? `linear-gradient(135deg,${color},${color}cc)` : `${C.silver}15`, border: allDone ? 'none' : `1px solid ${C.silver}22`, borderRadius: 12, color: allDone ? '#0a1220' : C.silver2, fontSize: 14, fontWeight: 700, cursor: allDone ? 'pointer' : 'default' }}>
        {allDone ? (nl ? '✓ Artikel begrepen!' : '✓ Article understood!') : `${understood.length}/${steps.length} ${nl ? 'begrepen' : 'understood'}`}
      </button>
    </div>
  );
}

// ── Quiz phase ────────────────────────────────────────────────────────────────
function QuizPhase({ item, nl, color, onDone }) {
  const [qIdx, setQIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const q = item.quiz[qIdx];

  const handleSelect = (optIdx) => {
    if (selected !== null) return;
    const isCorrect = !!q.opts[optIdx].correct;
    setSelected(optIdx);
    if (isCorrect) setScore(s => s + 1);
    setShowResult(true);
    setTimeout(() => {
      if (qIdx + 1 < item.quiz.length) {
        setQIdx(qIdx + 1);
        setSelected(null);
        setShowResult(false);
      } else {
        onDone(score + (isCorrect ? 1 : 0));
      }
    }, 1400);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ color: C.silver, fontSize: 12 }}>{nl ? 'Vraag' : 'Question'} {qIdx + 1}/{item.quiz.length}</div>
        <div style={{ color: color, fontSize: 12, fontWeight: 600 }}>{score} {nl ? 'correct' : 'correct'}</div>
      </div>
      <div style={{ background: _T.bg3, borderRadius: 14, padding: 16, marginBottom: 16, border: `1px solid ${_T.cardBorderBot}` }}>
        <div style={{ color: C.white, fontSize: 15, fontWeight: 600, lineHeight: 1.5 }}>
          {nl ? q.qNL : q.qEN}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {q.opts.map((opt, i) => {
          let bg = _T.bg3, border = _T.cardBorderBot, clr = C.white;
          if (showResult && selected === i) {
            bg = opt.correct ? '#4AC87A20' : '#C0505020';
            border = opt.correct ? '#4AC87A' : '#C05050';
            clr = opt.correct ? '#4AC87A' : '#C05050';
          } else if (showResult && opt.correct) {
            bg = '#4AC87A10'; border = '#4AC87A44'; clr = '#4AC87A';
          }
          return (
            <button key={i} onClick={() => handleSelect(i)}
              style={{ background: bg, border: `1.5px solid ${border}`, borderRadius: 12, padding: '12px 16px', cursor: selected === null ? 'pointer' : 'default', textAlign: 'left', color: clr, fontSize: 13, fontWeight: 500, transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 24, height: 24, borderRadius: '50%', border: `1.5px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0, fontWeight: 700 }}>
                {showResult && selected === i ? (opt.correct ? '✓' : '✗') : String.fromCharCode(65 + i)}
              </span>
              {nl ? opt.nl : opt.en}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function BusinessTrainingScreen({ lang, currentUserId, businessId }) {
  const nl = lang === 'nl';

  // Migrate from old object format to array
  const [done, setDone] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('sl_biz_training_done') || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch { return []; }
  });

  const [activeCat, setActiveCat]     = useState(null);
  const [activeItem, setActiveItem]   = useState(null);
  const [gamePhase, setGamePhase]     = useState('read'); // 'read' | 'practice' | 'quiz' | 'done'
  const [quizScore, setQuizScore]     = useState(0);
  const [earnedXP, setEarnedXP]       = useState(0);

  const isDone = (catId, idx) => done.some(d => d.id === `${catId}_${idx}`);

  const markDone = (cat, item, idx, xp) => {
    const key = `${cat.id}_${idx}`;
    if (isDone(cat.id, idx)) return; // already done
    const title = nl ? item.titleNL : item.titleEN;
    const entry = { id: key, cat: cat.id, title, ts: Date.now() };

    const newDone = [...done, entry];
    setDone(newDone);
    try { localStorage.setItem('sl_biz_training_done', JSON.stringify(newDone)); } catch {}

    // Athlete badge
    try {
      const hist = JSON.parse(localStorage.getItem('sl_ex_history') || '[]');
      hist.push({ id: `training_${key}_${Date.now()}`, type: 'training', cat: cat.id, title, ts: Date.now() });
      localStorage.setItem('sl_ex_history', JSON.stringify(hist));
    } catch {}

    // Business API (fire and forget)
    if (businessId && currentUserId) {
      completeTraining(businessId, { userId: currentUserId, categoryId: cat.id, itemTitle: title, xp }).catch(() => {});
    }
  };

  const openItem = (cat, item, idx) => {
    setActiveItem({ cat, item, idx });
    setGamePhase('read');
    setQuizScore(0);
    setEarnedXP(0);
  };

  // ── Game: Item detail view ──────────────────────────────────────────────────
  if (activeItem) {
    const { cat, item, idx } = activeItem;
    const color = cat.color;
    const xp = getXP(nl ? item.typeNL : item.typeEN);
    const alreadyDone = isDone(cat.id, idx);
    const mode = getPracticeMode(nl ? item.typeNL : item.typeEN);
    const isMindfulness = item.icon === '🧘';

    const goToPhase = (phase) => setGamePhase(phase);

    const handlePracticeDone = () => goToPhase('quiz');

    const handleQuizDone = (score) => {
      setQuizScore(score);
      const bonus = score >= item.quiz.length ? 10 : 0;
      setEarnedXP(xp + bonus);
      goToPhase('done');
    };

    const handleFinish = () => {
      markDone(cat, item, idx, earnedXP || xp);
      setActiveItem(null);
      setGamePhase('read');
    };

    return (
      <div style={{ padding: '0 0 32px' }}>
        <style>{CSS}</style>
        <button onClick={() => { setActiveItem(null); }}
          style={{ background: 'none', border: 'none', color: _T.accent, fontSize: 14, cursor: 'pointer', padding: '0 0 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
          ← {nl ? 'Terug' : 'Back'}
        </button>

        {/* Header */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: `${color}20`, border: `2px solid ${color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>{item.icon}</div>
          <div>
            <div style={{ color: C.white, fontSize: 16, fontWeight: 700 }}>{nl ? item.titleNL : item.titleEN}</div>
            <div style={{ color: color, fontSize: 11, fontWeight: 600, marginTop: 2 }}>{nl ? item.typeNL : item.typeEN} · +{xp} XP</div>
          </div>
          {alreadyDone && <div style={{ marginLeft: 'auto', background: `${color}20`, border: `1px solid ${color}44`, borderRadius: 8, padding: '4px 10px', fontSize: 11, color: color, fontWeight: 600 }}>✓ {nl ? 'Voltooid' : 'Done'}</div>}
        </div>

        {!alreadyDone && <PhaseBar phase={gamePhase} color={color} nl={nl} />}

        {/* Phase: Read */}
        {gamePhase === 'read' && (
          <>
            <div style={{ background: _T.bg3, borderRadius: 16, padding: 18, border: `1px solid ${_T.cardBorderBot}`, marginBottom: 16 }}>
              <div style={{ color: C.silver2, fontSize: 14, lineHeight: 1.8 }}>{nl ? item.bodyNL : item.bodyEN}</div>
            </div>
            <button onClick={() => alreadyDone ? setActiveItem(null) : goToPhase('practice')}
              style={{ width: '100%', padding: 13, background: `linear-gradient(135deg,${color},${color}cc)`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              {alreadyDone ? (nl ? '← Terug' : '← Back') : (nl ? '▶ Start oefening' : '▶ Start practice')}
            </button>
          </>
        )}

        {/* Phase: Practice */}
        {gamePhase === 'practice' && (
          <>
            <div style={{ color: C.white, fontSize: 14, fontWeight: 600, marginBottom: 14 }}>
              {nl ? '🏃 Oefenfase' : '🏃 Practice phase'}
            </div>
            {isMindfulness
              ? <BreathingExercise nl={nl} onDone={handlePracticeDone} />
              : mode === 'timer'
                ? <TimerPractice item={item} nl={nl} color={color} onDone={handlePracticeDone} />
                : mode === 'article'
                  ? <ArticlePractice item={item} nl={nl} color={color} onDone={handlePracticeDone} />
                  : <StepsPractice item={item} nl={nl} color={color} onDone={handlePracticeDone} />
            }
          </>
        )}

        {/* Phase: Quiz */}
        {gamePhase === 'quiz' && (
          <>
            <div style={{ color: C.white, fontSize: 14, fontWeight: 600, marginBottom: 14 }}>
              🧠 {nl ? 'Kennischeck' : 'Knowledge check'}
            </div>
            <QuizPhase item={item} nl={nl} color={color} onDone={handleQuizDone} />
          </>
        )}

        {/* Phase: Done */}
        {gamePhase === 'done' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 60, marginBottom: 16 }}>🎉</div>
            <div style={{ color: C.white, fontSize: 20, fontWeight: 800, marginBottom: 6, fontFamily: 'Georgia,serif' }}>
              {nl ? 'Geweldig gedaan!' : 'Great work!'}
            </div>
            <div style={{ background: `${color}15`, border: `1px solid ${color}44`, borderRadius: 16, padding: '16px 24px', margin: '16px auto', maxWidth: 260, display: 'inline-block' }}>
              <div style={{ color: color, fontSize: 32, fontWeight: 800 }}>+{earnedXP} XP</div>
              <div style={{ color: C.silver, fontSize: 12, marginTop: 4 }}>
                {nl ? `Quiz: ${quizScore}/${item.quiz.length} correct` : `Quiz: ${quizScore}/${item.quiz.length} correct`}
              </div>
            </div>
            <div style={{ color: C.silver, fontSize: 13, marginBottom: 24 }}>
              {nl ? 'Je voortgang is opgeslagen en telt mee voor je badges!' : 'Your progress is saved and counts toward your badges!'}
            </div>
            <button onClick={handleFinish}
              style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 14, fontWeight: 800, padding: '13px 32px', cursor: 'pointer' }}>
              {nl ? 'Terug naar training' : 'Back to training'}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Category detail view ────────────────────────────────────────────────────
  if (activeCat) {
    const completedCount = activeCat.items.filter((_, i) => isDone(activeCat.id, i)).length;
    return (
      <div style={{ padding: '0 0 32px' }}>
        <style>{CSS}</style>
        <button onClick={() => setActiveCat(null)}
          style={{ background: 'none', border: 'none', color: _T.accent, fontSize: 14, cursor: 'pointer', padding: '0 0 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
          ← {nl ? 'Terug' : 'Back'}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <div style={{ fontSize: 32 }}>{activeCat.icon}</div>
          <div>
            <div style={{ color: C.white, fontSize: 18, fontWeight: 700 }}>{nl ? activeCat.nl : activeCat.en}</div>
            <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>{nl ? activeCat.descNL : activeCat.descEN}</div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.silver, marginBottom: 12, paddingTop: 4 }}>
          <span>{completedCount}/{activeCat.items.length} {nl ? 'voltooid' : 'completed'}</span>
          <span style={{ color: activeCat.color, fontWeight: 700 }}>{Math.round(completedCount / activeCat.items.length * 100)}%</span>
        </div>
        <div style={{ height: 6, background: `${activeCat.color}20`, borderRadius: 4, marginBottom: 20 }}>
          <div style={{ width: `${(completedCount / activeCat.items.length) * 100}%`, height: '100%', background: activeCat.color, borderRadius: 4, transition: 'width 0.4s' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {activeCat.items.map((item, i) => {
            const done_ = isDone(activeCat.id, i);
            const xp = getXP(nl ? item.typeNL : item.typeEN);
            return (
              <div key={i} onClick={() => openItem(activeCat, item, i)}
                style={{ background: _T.bg3, borderRadius: 14, padding: '14px 16px', border: `1px solid ${done_ ? activeCat.color + '55' : _T.cardBorderBot}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, opacity: done_ ? 0.8 : 1 }}>
                <div style={{ width: 44, height: 44, borderRadius: 13, background: done_ ? `${activeCat.color}20` : `${activeCat.color}12`, border: `1px solid ${activeCat.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                  {done_ ? <span style={{ color: activeCat.color, fontSize: 20, fontWeight: 700 }}>✓</span> : item.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: done_ ? C.silver : C.white, fontSize: 14, fontWeight: 600 }}>{nl ? item.titleNL : item.titleEN}</div>
                  <div style={{ color: activeCat.color, fontSize: 10, marginTop: 3, fontWeight: 600 }}>{nl ? item.typeNL : item.typeEN}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: activeCat.color, fontWeight: 700 }}>+{xp} XP</div>
                  <span style={{ color: C.silver, fontSize: 16 }}>›</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Category overview ───────────────────────────────────────────────────────
  const totalItems = CATEGORIES.reduce((s, c) => s + c.items.length, 0);
  const totalDone  = CATEGORIES.reduce((s, c) => s + c.items.filter((_, i) => isDone(c.id, i)).length, 0);
  const totalXP    = done.reduce((s) => s + 35, 0); // avg XP per item

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>
      <div style={{ marginBottom: 20 }}>
        <div style={{ color: C.white, fontSize: 17, fontWeight: 700, marginBottom: 2 }}>
          {nl ? 'Training & Ontwikkeling' : 'Training & Development'}
        </div>
        <div style={{ color: C.silver, fontSize: 12, marginBottom: 8 }}>
          {totalDone}/{totalItems} {nl ? 'modules · ' : 'modules · '} {totalXP} XP {nl ? 'verdiend' : 'earned'}
        </div>
        <div style={{ height: 6, background: `${_T.accent}18`, borderRadius: 3 }}>
          <div style={{ width: `${totalItems > 0 ? (totalDone / totalItems) * 100 : 0}%`, height: '100%', background: `linear-gradient(90deg,${_T.grad1},${_T.grad2})`, borderRadius: 3, transition: 'width 0.4s' }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {CATEGORIES.map(cat => {
          const completedCount = cat.items.filter((_, i) => isDone(cat.id, i)).length;
          const pct = Math.round(completedCount / cat.items.length * 100);
          return (
            <div key={cat.id} onClick={() => setActiveCat(cat)}
              style={{ background: _T.bg3, borderRadius: 16, padding: '16px 14px', border: `1px solid ${_T.cardBorderBot}`, cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, height: 3, width: `${pct}%`, background: cat.color, borderRadius: '16px 16px 0 0', transition: 'width 0.4s' }} />
              <div style={{ fontSize: 28, marginBottom: 10 }}>{cat.icon}</div>
              <div style={{ color: C.white, fontSize: 14, fontWeight: 700, marginBottom: 3 }}>{nl ? cat.nl : cat.en}</div>
              <div style={{ color: C.silver, fontSize: 10, marginBottom: 10, lineHeight: 1.4 }}>{nl ? cat.descNL : cat.descEN}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 10, color: C.silver }}>{completedCount}/{cat.items.length}</span>
                <span style={{ fontSize: 11, color: cat.color, fontWeight: 700 }}>{pct}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
